<script lang="ts">
  /**
   * ProjectRail - Right panel for viewing/editing project tasks.
   *
   * IMPORTANT: Task mutations use optimistic updates + cache sync.
   * After every task mutation, we call setProjectTasks() to sync the cache.
   * This ensures other components (ExpandableProjectChip, ProjectCard, tooltips)
   * see the updated task data immediately.
   *
   * @see docs/FRONTEND_ARCHITECTURE.md for the full pattern
   * @see lib/taskApi.ts for centralized mutation helpers (preferred for new code)
   */
  import { router, page } from '@inertiajs/svelte';
  import { toast } from 'svelte-sonner';
  import { tick } from 'svelte';
  import type { Project, Task, TaskPriority, TaskViewPreferences } from '../lib/types';
  import { categoryColors, categoryIcons } from '../lib/utils';
  import { setCompactTaskDragImage } from '../lib/dragImage';
  import { sortTasks, groupTasks, isDragReorderAllowed } from '../lib/taskSort';
  import TaskViewOptions from './TaskViewOptions.svelte';
  import CopyTasksMenu from './CopyTasksMenu.svelte';
  import TaskDetailPopover from './TaskDetailPopover.svelte';
  import NowToggle from './NowToggle.svelte';
  import { jsonHeaders, readApiErrorMessage } from '../lib/apiHelpers';
  import { recentlyAddedTasks, markTaskRecentlyAdded } from '../lib/stores/recentlyAddedTask';
  import { trackTaskCreation } from '../lib/pendingTaskCreations';
  import {
    isProjectRailOpen,
    projectRailData,
    closeProjectRail,
    openProjectModalForEdit,
    openProjectModalForConvertFromTask,
  } from '../lib/stores/ui';
  // Cache sync: setProjectTasks updates the shared cache so other components
  // (ExpandableProjectChip, ProjectCard, ProjectTooltip) reflect changes immediately
  import {
    setProjectTasks,
    invalidateProjectTasks,
    subscribeToCachedTasks,
    getCachedTasks,
  } from '../lib/stores/projectTasksCache';
  import {
    completeTask,
    markTaskWontDo,
    duplicateTask,
    deleteTask as deleteTaskApi,
    moveTaskToProject as moveTaskToProjectApi,
  } from '../lib/taskApi';
  import MoveToProjectPicker from './MoveToProjectPicker.svelte';
  import MoveToProjectPanel from './MoveToProjectPanel.svelte';
  import { removeTaskAfterConversion } from '../lib/taskActions';
  import Icon from './Icon.svelte';
  import TaskCopyActionRow from './TaskCopyActionRow.svelte';
  import RecentMoveMenuItem from './RecentMoveMenuItem.svelte';
  import { recordRecentMove } from '../lib/stores/recentMove';
  import { isCoarsePointer, tapAlternatives, viewport } from '../lib/stores/viewport';
  import ActionSheet from './mobile/ActionSheet.svelte';
  import { moveUpDownActions, type MoveDirection, type SheetAction } from '../lib/sheetActions';
  import { projectMoveActions } from '../lib/projectStatusActions';
  import MobileAddTask, { type NewTaskDraft } from './mobile/MobileAddTask.svelte';
  import BulkMoveTasksConfirmModal from './BulkMoveTasksConfirmModal.svelte';
  import ResponsiveOverlay from './ResponsiveOverlay.svelte';

  interface Props {
    allProjects: Project[];
    /** Render as an overlay, used by the board canvas so opening it doesn't reflow the layout. */
    floating?: boolean;
  }

  let { allProjects, floating = false }: Props = $props();

  let isOpen = $derived($isProjectRailOpen);
  let data = $derived($projectRailData);

  // Fallback resolution: when the rail is opened for a project that isn't in `allProjects`
  // (e.g. Timeline Days-panel projects scheduled outside the visible timeline window), we
  // fetch it by id from GET /projects/:id.json instead of silently rendering nothing.
  let fetchedProject = $state<Project | null>(null);
  let projectFetchFailed = $state(false);

  // Find the project from live props (preferred: reactive, zero-latency),
  // falling back to the fetched copy when the props don't contain the id.
  let project = $derived.by(() => {
    if (!data) return null;
    return allProjects.find((p) => p.id === data.projectId) || fetchedProject;
  });

  // Tasks state — loaded via fetch, managed locally (no Inertia)
  let tasks = $state<Task[]>([]);
  let tasksLoading = $state(false);
  // Track which task's "move to project" picker is open
  let movePickerTaskId = $state<string | null>(null);

  // Bulk move incomplete tasks UI state
  let bulkMovePickerOpen = $state(false);
  let bulkMoveAnchorEl = $state<HTMLElement | null>(null);
  let bulkMoveTargetProjectId = $state<string | null>(null);
  let bulkMoveTargetProjectTitle = $state('');
  let showBulkMoveConfirm = $state(false);

  // New task input
  let newTaskTitle = $state('');
  let newTaskInput: HTMLInputElement | undefined = $state();
  let isAddingTask = $state(false);

  // Expanded add-task form (expand button or Shift+Enter)
  let isAddFormExpanded = $state(false);
  let newTaskNotes = $state('');
  let newTaskPriority = $state<TaskPriority>('none');
  let newTaskNotesInput: HTMLTextAreaElement | undefined = $state();

  function expandAddForm() {
    if (!newTaskTitle.trim()) return;
    isAddFormExpanded = true;
    tick().then(() => newTaskNotesInput?.focus());
  }

  function collapseAddForm() {
    isAddFormExpanded = false;
    newTaskNotes = '';
    newTaskPriority = 'none';
  }

  // Track which task's menu is open (null = all closed)
  let openMenuId = $state<string | null>(null);

  function toggleMenu(taskId: string) {
    openMenuId = openMenuId === taskId ? null : taskId;
    movePickerTaskId = null;
  }

  function closeMenus() {
    openMenuId = null;
    movePickerTaskId = null;
    bulkMovePickerOpen = false;
    bulkMoveAnchorEl = null;
  }

  // Task detail popover
  let popoverTask = $state<Task | null>(null);
  let popoverAnchorEl = $state<HTMLElement | null>(null);
  // Set when a row's ⋯ opens the task sheet on its menu (mobile), see openTaskMenu
  let popoverMenu = $state<'actions' | null>(null);
  let popoverMenuActions = $state<SheetAction[]>([]);

  function openTaskPopover(
    task: Task,
    anchorEl: HTMLElement,
    menu: { actions: SheetAction[] } | null = null,
  ) {
    popoverMenu = menu ? 'actions' : null;
    popoverMenuActions = menu?.actions ?? [];
    // Toggle off if clicking the same task
    if (popoverTask?.id === task.id) {
      closeTaskPopover();
      return;
    }

    // Prefer the shared cache at click time so external edits (e.g. from global
    // search's task modal) cannot open this popover with a stale task object.
    const latestTask = data?.projectId
      ? getCachedTasks(data.projectId).tasks.find((t) => t.id === task.id) || task
      : task;
    popoverTask = latestTask;
    popoverAnchorEl = anchorEl;
  }

  function closeTaskPopover() {
    popoverTask = null;
    popoverAnchorEl = null;
  }

  async function handlePopoverUpdate(task: Task, updates: Partial<Task>) {
    if (!data) return;
    const prevTasks = [...tasks];
    const projectId = data.projectId;

    // Optimistic update
    tasks = tasks.map((t) => (t.id === task.id ? { ...t, ...updates } : t));
    setProjectTasks(projectId, tasks);

    try {
      const response = await fetch(`/projects/${projectId}/tasks/${task.id}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ task: updates }),
      });
      if (response.ok) {
        const updated: Task = await response.json();
        tasks = tasks.map((t) => (t.id === task.id ? updated : t));
        setProjectTasks(projectId, tasks);
        // Update popover task if still open
        if (popoverTask?.id === task.id) {
          popoverTask = updated;
        }
      } else {
        tasks = prevTasks;
        setProjectTasks(projectId, prevTasks);
        toast.error(await readApiErrorMessage(response, 'Failed to save changes'));
      }
    } catch {
      tasks = prevTasks;
      setProjectTasks(projectId, prevTasks);
      toast.error('Failed to save changes');
    }
  }

  function requestDeleteTask(task: Task) {
    closeMenus();
    deleteTask(task);
  }

  function convertTaskToProject(task: Task) {
    closeMenus();
    openProjectModalForConvertFromTask(task.title, task.notes || '', () =>
      removeTaskAfterConversion(task),
    );
  }

  // Load tasks when project changes, and auto-focus input
  // eslint-disable-next-line momentium/no-unguarded-effect -- conditional data fetch: loads tasks when rail opens or project changes, resets UI state
  $effect(() => {
    if (isOpen && data?.projectId) {
      loadTasks(data.projectId);
      // Reset expanded form when switching projects
      collapseAddForm();
    }
  });

  // Fetch the project when its id can't be resolved from allProjects (fallback path).
  // Re-runs whenever the rail target or the allProjects prop changes, so the fetch only
  // fires while the id remains unresolved.
  // eslint-disable-next-line momentium/no-unguarded-effect -- conditional data fetch: loads project only when the id is missing from allProjects
  $effect(() => {
    const projectId = data?.projectId;
    const known = projectId ? allProjects.some((p) => p.id === projectId) : false;
    fetchedProject = null;
    projectFetchFailed = false;
    if (!projectId || known) return;
    fetchProject(projectId);
  });

  async function fetchProject(projectId: string) {
    // Known trade-off: loadTasks() hits the same GET /projects/:id.json endpoint,
    // so the fallback path issues a duplicate request. Only affects the rare
    // out-of-props path; de-duping would couple task loading to project
    // resolution, so it's deferred until request volume justifies it.
    try {
      const response = await fetch(`/projects/${projectId}`, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      // Ignore a stale response if the rail has moved on to a different project.
      if (data?.projectId !== projectId) return;
      if (!json.project) throw new Error('Missing project in response');
      fetchedProject = json.project;
    } catch (e) {
      if (data?.projectId !== projectId) return;
      projectFetchFailed = true;
      console.error('Failed to load project:', e);
      toast.error('Could not load that project');
    }
  }

  // Auto-focus the task input when rail opens (not on touch: the keyboard would
  // cover half the sheet before the user asked to type)
  // eslint-disable-next-line momentium/no-unguarded-effect -- DOM focus: must focus input after tick() when rail opens and tasks finish loading
  $effect(() => {
    if (isOpen && !tasksLoading && !isCoarsePointer()) {
      tick().then(() => newTaskInput?.focus());
    }
  });

  async function loadTasks(projectId: string) {
    tasksLoading = true;
    try {
      const response = await fetch(`/projects/${projectId}`, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (response.ok) {
        const json = await response.json();
        const fetchedTasks = json.tasks || [];
        tasks = fetchedTasks;
        // Sync to cache so other components (ExpandableProjectChip, etc.) see the data.
        // The cache merge protects newer local edits from being overwritten by an older
        // in-flight rail load response; the cache subscription then reconciles this local list.
        setProjectTasks(projectId, fetchedTasks);
      }
    } catch (e) {
      console.error('Failed to load tasks:', e);
    } finally {
      tasksLoading = false;
      // Focus input after load completes (see the auto-focus effect above)
      await tick();
      if (!isCoarsePointer()) newTaskInput?.focus();
    }
  }

  // Keep the rail's local task list in sync with external task mutations
  // (e.g. edits made from global search's task modal).
  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: syncs projectTasksCache updates into rail local state for the open project
  $effect(() => {
    if (!data?.projectId) return;

    return subscribeToCachedTasks(data.projectId, (cached) => {
      if (cached.fetched) {
        tasks = cached.tasks;
      }
    });
  });

  // Task view preferences from shared Inertia props
  const defaultPrefs: TaskViewPreferences = {
    sort_by: 'position',
    sort_direction: 'asc',
    group_by: 'none',
  };
  let taskViewPrefs = $state<TaskViewPreferences>(
    ($page.props as any).preferences?.taskView ?? defaultPrefs,
  );

  // Sync from page props when they change (e.g., after Inertia navigation)
  // eslint-disable-next-line momentium/no-unguarded-effect -- Inertia prop sync: taskViewPrefs must update from server when page props change (e.g., after navigation). Same pattern as Show.svelte.
  $effect(() => {
    const serverPrefs = ($page.props as any).preferences?.taskView;
    if (serverPrefs) {
      taskViewPrefs = serverPrefs;
    }
  });

  function handlePrefsUpdate(newPrefs: TaskViewPreferences) {
    taskViewPrefs = newPrefs;
  }

  function clearSortPref() {
    const updated: TaskViewPreferences = {
      ...taskViewPrefs,
      sort_by: 'position',
      sort_direction: 'asc',
    };
    handlePrefsUpdate(updated);
    persistPrefs(updated);
  }

  function clearGroupPref() {
    const updated: TaskViewPreferences = { ...taskViewPrefs, group_by: 'none' };
    handlePrefsUpdate(updated);
    persistPrefs(updated);
  }

  function persistPrefs(prefs: TaskViewPreferences) {
    fetch('/preferences/task_view', {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify(prefs),
    }).catch((e) => console.error('Failed to save preferences:', e));
  }

  let canDragReorder = $derived(isDragReorderAllowed(taskViewPrefs));

  // Reorder confirmation modal (shown when dragging while non-position sort is active)
  let pendingReorder = $state<{ fromIdx: number; toIdx: number } | null>(null);

  // Separate incomplete and completed tasks
  let rawIncompleteTasks = $derived(tasks.filter((t) => t.status === 'todo'));
  let incompleteTasks = $derived(sortTasks(rawIncompleteTasks, taskViewPrefs));
  let taskGroups = $derived(groupTasks(rawIncompleteTasks, taskViewPrefs));
  let isGrouped = $derived(taskViewPrefs.group_by !== 'none');

  let completedTasks = $derived(
    tasks
      .filter((t) => t.status !== 'todo')
      .sort((a, b) => {
        const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return bTime - aTime;
      }),
  );

  let showCompleted = $derived(taskViewPrefs.show_completed ?? false);

  // === Task Actions (all via fetch, no Inertia) ===

  async function addTask() {
    if (!newTaskTitle.trim() || !data || isAddingTask) return;
    isAddingTask = true;

    const title = newTaskTitle.trim();
    const notes = isAddFormExpanded ? newTaskNotes.trim() || null : null;
    const priority = isAddFormExpanded ? newTaskPriority : ('none' as TaskPriority);
    newTaskTitle = '';
    collapseAddForm();

    try {
      await createTask({ title, notes, priority, tagNames: [] });
    } finally {
      isAddingTask = false;
      // Always refocus the input for rapid entry
      await tick();
      newTaskInput?.focus();
    }
  }

  /** Optimistic add, shared by the inline input (desktop) and MobileAddTask's sheet. */
  async function createTask({ title, notes, priority, tagNames }: NewTaskDraft) {
    if (!data) return;
    const projectId = data.projectId;

    // Optimistic add
    const tempId = `temp-${crypto.randomUUID()}`;
    const tempTask: Task = {
      id: tempId,
      projectId,
      title,
      notes,
      status: 'todo',
      priority,
      tags: tagNames.map((name) => ({ id: name, name })),
      position: incompleteTasks.length,
      isActive: false,
      completedAt: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    tasks = [...tasks, tempTask];
    setProjectTasks(projectId, tasks);
    markTaskRecentlyAdded(tempId);
    const settleCreation = trackTaskCreation(tempId);
    let saved: Task | null = null;

    try {
      const response = await fetch(`/projects/${projectId}/tasks`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({
          task: { title, notes, priority, ...(tagNames.length ? { tag_names: tagNames } : {}) },
        }),
      });

      if (response.ok) {
        const created: Task = await response.json();
        // Replace temp task with real one
        tasks = tasks.map((t) => (t.id === tempId ? created : t));
        setProjectTasks(projectId, tasks);
        markTaskRecentlyAdded(created.id);
        saved = created;
      } else {
        // Remove optimistic task on error
        tasks = tasks.filter((t) => t.id !== tempId);
        setProjectTasks(projectId, tasks);
        toast.error(await readApiErrorMessage(response, 'Failed to add task'));
      }
    } catch {
      tasks = tasks.filter((t) => t.id !== tempId);
      setProjectTasks(projectId, tasks);
      toast.error('Failed to add task');
    } finally {
      // Always settle, or mutations queued on this row would wait forever.
      settleCreation(saved);
    }
  }

  async function toggleTask(task: Task) {
    await toggleTaskWithUpdates(task);
  }

  async function toggleTaskWithUpdates(task: Task, pendingUpdates?: Partial<Task>) {
    if (!data) return;
    await completeTask(data.projectId, task.id, tasks, pendingUpdates);
  }

  async function handleDuplicateTask(task: Task) {
    if (!data) return;
    closeMenus();
    await duplicateTask(data.projectId, task.id, tasks);
  }

  async function markWontDo(task: Task) {
    await markWontDoWithUpdates(task);
  }

  async function markWontDoWithUpdates(task: Task, pendingUpdates?: Partial<Task>) {
    if (!data) return;
    closeMenus();
    await markTaskWontDo(data.projectId, task.id, tasks, pendingUpdates);
  }

  function deleteTask(task: Task) {
    if (!data) return;
    closeMenus();
    deleteTaskApi(data.projectId, task.id, tasks);
  }

  async function setPriority(task: Task, newPriority: TaskPriority) {
    if (!data || task.priority === newPriority) return;
    closeMenus();
    const prevTasks = [...tasks];
    const projectId = data.projectId;

    tasks = tasks.map((t) => (t.id === task.id ? { ...t, priority: newPriority } : t));
    setProjectTasks(projectId, tasks);

    try {
      const response = await fetch(`/projects/${projectId}/tasks/${task.id}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ task: { priority: newPriority } }),
      });
      if (response.ok) {
        const updated: Task = await response.json();
        tasks = tasks.map((t) => (t.id === task.id ? updated : t));
        setProjectTasks(projectId, tasks);
      } else {
        tasks = prevTasks;
        setProjectTasks(projectId, prevTasks);
      }
    } catch {
      tasks = prevTasks;
      setProjectTasks(projectId, prevTasks);
    }
  }

  function moveTaskToProject(task: Task, targetProjectId: string, targetProjectTitle?: string) {
    if (!data) return;
    closeMenus();
    movePickerTaskId = null;
    moveTaskToProjectApi(data.projectId, targetProjectId, task.id, tasks, { targetProjectTitle });
  }

  /** Direct move via the recent-move shortcut (no picker). */
  function moveTaskToRecent(task: Task, targetProjectId: string, targetProjectTitle: string) {
    moveTaskToProject(task, targetProjectId, targetProjectTitle);
  }

  async function confirmBulkMoveIncompleteTasks() {
    if (!data || !bulkMoveTargetProjectId) return;
    const projectId = data.projectId;
    showBulkMoveConfirm = false;

    try {
      const response = await fetch(`/projects/${projectId}/tasks/move_incomplete`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ target_project_id: bulkMoveTargetProjectId }),
      });

      if (!response.ok) throw new Error('Failed to move tasks');

      tasks = tasks.filter((t) => t.status !== 'todo');
      setProjectTasks(projectId, tasks);
      invalidateProjectTasks(bulkMoveTargetProjectId);
      bulkMoveTargetProjectId = null;
      bulkMoveTargetProjectTitle = '';
      toast.success('Moved incomplete tasks');
    } catch {
      toast.error('Failed to move incomplete tasks');
    }
  }

  // === Drag reorder ===
  let dragIdx = $state<number | null>(null);
  let dragOverIdx = $state<number | null>(null);

  function handleDragStart(e: DragEvent, idx: number) {
    dragIdx = idx;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(idx));
      const task = incompleteTasks[idx];
      setCompactTaskDragImage(e, task.title, task.priority);
    }
  }

  function handleDragOver(e: DragEvent, idx: number) {
    e.preventDefault();
    dragOverIdx = idx;
  }

  async function handleDrop(e: DragEvent, idx: number) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx || !data) return;
    requestReorder(dragIdx, idx);
  }

  /** Drop and Move up / Move down: reorders, after confirming when a sort is active. */
  function requestReorder(fromIdx: number, toIdx: number) {
    // If non-default sort/group is active, show confirmation modal
    if (!canDragReorder) {
      pendingReorder = { fromIdx, toIdx };
      dragIdx = null;
      dragOverIdx = null;
      return;
    }

    executeReorder(fromIdx, toIdx);
  }

  function moveTaskBy(idx: number, direction: MoveDirection) {
    requestReorder(idx, idx + direction);
  }

  async function executeReorder(fromIdx: number, toIdx: number) {
    if (!data) return;

    const reordered = [...incompleteTasks];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    const newTaskIds = reordered.map((t) => t.id);
    const prevTasks = [...tasks];
    const projectId = data.projectId;

    // Optimistic position update
    tasks = tasks.map((t) => {
      const newPos = newTaskIds.indexOf(t.id);
      return newPos >= 0 ? { ...t, position: newPos } : t;
    });
    setProjectTasks(projectId, tasks);

    dragIdx = null;
    dragOverIdx = null;

    try {
      const response = await fetch(`/projects/${projectId}/tasks/reorder`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ task_ids: newTaskIds }),
      });
      if (!response.ok) {
        tasks = prevTasks;
        setProjectTasks(projectId, prevTasks);
      }
    } catch {
      tasks = prevTasks;
      setProjectTasks(projectId, prevTasks);
    }
  }

  function confirmReorder() {
    if (!pendingReorder) return;
    const { fromIdx, toIdx } = pendingReorder;
    pendingReorder = null;
    // Switch to manual ordering first, then execute the reorder
    const manualPrefs: TaskViewPreferences = {
      sort_by: 'position',
      sort_direction: 'asc',
      group_by: 'none',
    };
    handlePrefsUpdate(manualPrefs);
    persistPrefs(manualPrefs);
    executeReorder(fromIdx, toIdx);
  }

  function cancelReorder() {
    pendingReorder = null;
  }

  function handleDragEnd() {
    dragIdx = null;
    dragOverIdx = null;
  }

  // === Mobile ⋯ menus (plans/mobile-v2.md §3.4–3.5) ===
  function taskMoveActions(idx: number): SheetAction[] {
    return moveUpDownActions(idx, incompleteTasks.length, (direction) =>
      moveTaskBy(idx, direction),
    );
  }

  // A task row's ⋯ opens the task sheet on its own ⋯ menu, so both are the same
  // menu; the row adds Move up / Move down (the tap path for drag-to-reorder).
  function openTaskMenu(task: Task, idx: number, trigger: HTMLElement) {
    if (!$viewport.isMobile) {
      toggleMenu(task.id);
      return;
    }
    closeMenus();
    openTaskPopover(task, trigger, { actions: taskMoveActions(idx) });
  }

  // The project's ⋯: Move to <status> / Schedule… (the tap path for dragging chips)
  let projectMenuOpen = $state(false);

  // Close menus when clicking outside
  function handleRailClick() {
    if (openMenuId !== null) {
      openMenuId = null;
      movePickerTaskId = null;
    }
  }

  // Priority display
  const priorityIcons: Record<TaskPriority, string> = {
    none: '',
    low: '🔵',
    medium: '🟠',
    high: '🔴',
  };

  const priorityBorderColors: Record<TaskPriority, string> = {
    none: '#d1d5db',
    low: '#60a5fa',
    medium: '#fb923c',
    high: '#ef4444',
  };

  const priorityHoverBgs: Record<TaskPriority, string> = {
    none: 'rgba(209, 213, 219, 0.12)',
    low: 'rgba(96, 165, 250, 0.08)',
    medium: 'rgba(251, 146, 60, 0.08)',
    high: 'rgba(239, 68, 68, 0.08)',
  };

  // Status indicators
  const statusLabels: Record<string, string> = {
    planned: 'Planned',
    active: 'Active',
    done: 'Done',
  };

  const STATUS_DOT_COLORS: Record<string, string> = {
    planned: '#3b82f6',
    active: '#d97706',
    done: '#16a34a',
  };

  const CATEGORY_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
    tech: { bg: '#dbeafe', text: '#1e40af' },
    health: { bg: '#dcfce7', text: '#166534' },
    personal: { bg: '#fce7f3', text: '#9d174d' },
    finance: { bg: '#fef9c3', text: '#854d0e' },
    travel: { bg: '#e0e7ff', text: '#3730a3' },
    other: { bg: '#f3f4f6', text: '#374151' },
  };
</script>

{#snippet taskItem(task: Task, idx: number)}
  <div
    class="task-item {$recentlyAddedTasks.has(task.id) ? 'just-added-task' : ''}"
    class:drag-over={dragOverIdx === idx && dragIdx !== idx}
    draggable={idx >= 0 && !$viewport.isMobile}
    ondragstart={(e) => idx >= 0 && handleDragStart(e, idx)}
    ondragover={(e) => idx >= 0 && handleDragOver(e, idx)}
    ondrop={(e) => idx >= 0 && handleDrop(e, idx)}
    ondragend={handleDragEnd}
    role="listitem"
  >
    <!-- No drag-to-reorder in the mobile sheet (touch). -->
    {#if idx >= 0 && !$viewport.isMobile}
      <button type="button" class="drag-handle" tabindex="-1" title="Drag to reorder">⠿</button>
    {/if}
    <button
      type="button"
      class="task-checkbox"
      style="--cb-border: {priorityBorderColors[task.priority]}; --cb-hover-bg: {priorityHoverBgs[
        task.priority
      ]};"
      onclick={() => toggleTask(task)}
      title="Mark done"
    >
      <span class="checkbox-inner"></span>
    </button>
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <span
      class="task-title"
      data-task-popover-id={task.id}
      onclick={(e) => openTaskPopover(task, e.currentTarget as HTMLElement)}
      role="button"
      tabindex="0">{task.title}</span
    >
    {#if task.notes}
      <span class="notes-indicator" title="Has notes">
        <svg viewBox="0 0 16 16" fill="currentColor">
          <path
            d="M4 1h8a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1zm1 3v1h6V4H5zm0 3v1h6V7H5zm0 3v1h4v-1H5z"
          />
        </svg>
      </span>
    {/if}
    <!-- Task actions dropdown -->
    <div class="task-actions" class:menu-open={openMenuId === task.id}>
      <button
        type="button"
        class="task-menu-btn hover-reveal"
        onclick={(e) => {
          e.stopPropagation();
          openTaskMenu(task, idx, e.currentTarget as HTMLElement);
        }}
        title="Task actions"
        aria-label="Actions for {task.title}">⋯</button
      >
      <div
        class="task-menu"
        class:visible={openMenuId === task.id}
        class:picking={movePickerTaskId === task.id}
      >
        {#if movePickerTaskId === task.id && data}
          <MoveToProjectPanel
            excludeProjectId={data.projectId}
            onSelect={(id, title) => moveTaskToProject(task, id, title)}
            onBack={() => (movePickerTaskId = null)}
          />
        {:else}
          <!-- Touch without drag handles (e.g. a tablet): Move up / Move down -->
          {#if $tapAlternatives && idx >= 0}
            {#each taskMoveActions(idx) as move (move.label)}
              <button
                type="button"
                class="menu-item"
                onclick={() => {
                  closeMenus();
                  move.onSelect();
                }}
              >
                <Icon name={move.icon ?? 'more'} />{move.label}
              </button>
            {/each}
            <div class="menu-divider"></div>
          {/if}
          <div class="menu-label">Priority:</div>
          <div class="priority-row">
            <button
              type="button"
              class="priority-option"
              class:active={task.priority === 'none'}
              onclick={() => setPriority(task, 'none')}
              title="No priority">—</button
            >
            <button
              type="button"
              class="priority-option"
              class:active={task.priority === 'low'}
              onclick={() => setPriority(task, 'low')}
              title="Low">🔵</button
            >
            <button
              type="button"
              class="priority-option"
              class:active={task.priority === 'medium'}
              onclick={() => setPriority(task, 'medium')}
              title="Medium">🟠</button
            >
            <button
              type="button"
              class="priority-option"
              class:active={task.priority === 'high'}
              onclick={() => setPriority(task, 'high')}
              title="High">🔴</button
            >
          </div>
          <div class="menu-divider"></div>
          <NowToggle {task} />
          <button type="button" class="menu-item" onclick={() => handleDuplicateTask(task)}>
            <Icon name="duplicate" />Duplicate task
          </button>
          <TaskCopyActionRow {task} onDone={closeMenus} />
          <button type="button" class="menu-item" onclick={() => markWontDo(task)}>
            <Icon name="wont-do" />Won't do
          </button>
          <button
            type="button"
            class="menu-item menu-item-danger"
            onclick={() => requestDeleteTask(task)}
          >
            <Icon name="trash" />Delete
          </button>
          <div class="menu-divider"></div>
          {#if data}
            <RecentMoveMenuItem
              currentProjectId={data.projectId}
              onMove={(projectId, projectTitle) => moveTaskToRecent(task, projectId, projectTitle)}
            />
          {/if}
          <button
            type="button"
            class="menu-item"
            onclick={(e) => {
              e.stopPropagation();
              movePickerTaskId = task.id;
            }}
          >
            <Icon name="move-to-project" />Move to project…
            <Icon name="chevron-right" size={12} class="menu-trail" />
          </button>
          <button type="button" class="menu-item" onclick={() => convertTaskToProject(task)}>
            <Icon name="convert" />Convert to project
          </button>
        {/if}
      </div>
    </div>
  </div>
{/snippet}

<!-- Desktop: right rail (or a floating panel on boards). Mobile: full-height bottom sheet. -->
<ResponsiveOverlay
  open={isOpen && !!project}
  onClose={closeProjectRail}
  detents={['full']}
  label={project ? `Project: ${project.title}` : 'Project'}
>
  {#snippet desktop(content)}
    {#if isOpen && project}
      {@render content()}
    {/if}
  {/snippet}

  {#if project}
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions, a11y_click_events_have_key_events -->
    <aside
      class="project-rail"
      class:floating={floating && !$viewport.isMobile}
      class:in-sheet={$viewport.isMobile}
      class:archived={project.isArchived}
      data-sheet-fill
      onclick={handleRailClick}
    >
      <!-- Header -->
      <header class="rail-header" data-sheet-drag-handle>
        <div class="header-title-row">
          <h2 class="header-title" data-sheet-title>{project.title}</h2>
          {#if $viewport.isMobile}
            <!-- Tap alternative to dragging the project between Focus columns / months -->
            <button
              type="button"
              class="project-menu-btn"
              aria-label="Project actions"
              title="Project actions"
              onclick={(e) => {
                e.stopPropagation();
                projectMenuOpen = true;
              }}
            >
              <Icon name="more" size={18} />
            </button>
          {/if}
          <button
            type="button"
            class="close-btn"
            onclick={closeProjectRail}
            title="Close panel"
            data-sheet-close
          >
            {#if $viewport.isMobile}<Icon name="close" />{:else}✕{/if}
          </button>
        </div>
        <div class="header-meta">
          <span class="header-status">
            <span
              class="status-dot"
              style="background: {STATUS_DOT_COLORS[project.status] || '#9ca3af'};"
            ></span>
            {statusLabels[project.status] || project.status}
          </span>
          {#if project.isArchived}
            <span class="meta-sep">·</span>
            <span class="archived-pill" title="This project is archived"
              ><Icon name="archive" size={10} />Archived</span
            >
          {/if}
          <span class="meta-sep">·</span>
          <span
            class="header-category-badge"
            style="background: {(
              CATEGORY_BADGE_COLORS[project.category] || CATEGORY_BADGE_COLORS.other
            ).bg}; color: {(CATEGORY_BADGE_COLORS[project.category] || CATEGORY_BADGE_COLORS.other)
              .text};"
          >
            {categoryIcons[project.category]}
            {project.category}
          </span>
          {#if project.isPriority}
            <span class="meta-sep">·</span>
            <span class="priority-star">⭐</span>
          {/if}
        </div>
        {#if project.notes}
          <p class="header-notes">{project.notes}</p>
        {/if}
        <div class="header-actions">
          <button
            type="button"
            class="header-action-btn"
            onclick={() => openProjectModalForEdit(project!)}
          >
            <Icon name="edit" size={12} />Edit
          </button>
          <button
            type="button"
            class="header-action-btn"
            onclick={() => router.get(`/projects/${project!.id}`)}
          >
            📄 Full page
          </button>
        </div>
      </header>

      <!-- Tasks Section -->
      <div class="rail-content custom-scrollbar">
        <div class="tasks-section-header">
          <div class="tasks-top-row">
            <h3>Tasks</h3>
            <div class="tasks-top-actions">
              <TaskViewOptions
                preferences={taskViewPrefs}
                onUpdate={handlePrefsUpdate}
                getCopyTasks={() => taskGroups.flatMap((g) => g.tasks)}
                copyHeading={project?.title}
                copyDisabled={tasksLoading}
                onBulkMoveClick={(anchorEl) => {
                  if (incompleteTasks.length === 0) return;
                  bulkMoveAnchorEl = anchorEl;
                  bulkMovePickerOpen = true;
                }}
              />
            </div>
          </div>

          <!-- Progress bar -->
          {#if incompleteTasks.length > 0 || completedTasks.length > 0}
            {@const total = incompleteTasks.length + completedTasks.length}
            {@const doneCount = completedTasks.length}
            {@const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0}
            <div class="progress-row">
              <div class="progress-bar-track">
                <div class="progress-bar-fill" style="width: {pct}%"></div>
              </div>
              <span class="progress-label">{doneCount}/{total}</span>
            </div>
          {/if}

          <!-- Active view indicator (only when non-default) -->
          {#if !canDragReorder || isGrouped}
            <div class="active-view-chips">
              {#if taskViewPrefs.sort_by !== 'position'}
                <span class="view-chip">
                  {taskViewPrefs.sort_direction === 'desc' ? '↓' : '↑'}
                  {taskViewPrefs.sort_by === 'created_at'
                    ? 'Created'
                    : taskViewPrefs.sort_by === 'priority'
                      ? 'Priority'
                      : 'Title'}
                  <button
                    type="button"
                    class="chip-dismiss"
                    onclick={clearSortPref}
                    title="Clear sort">✕</button
                  >
                </span>
              {/if}
              {#if isGrouped}
                <span class="view-chip">
                  ▤ By {taskViewPrefs.group_by === 'priority' ? 'priority' : taskViewPrefs.group_by}
                  <button
                    type="button"
                    class="chip-dismiss"
                    onclick={clearGroupPref}
                    title="Clear grouping">✕</button
                  >
                </span>
              {/if}
            </div>
          {/if}
        </div>

        {#if tasksLoading}
          <div class="loading-state">
            <span class="loading-spinner">⟳</span>
            Loading tasks...
          </div>
        {:else}
          <!-- Add task input -->
          {#if $viewport.isMobile}
            <MobileAddTask onSubmit={createTask} />
          {:else}
            <div class="add-task-row" class:expanded={isAddFormExpanded}>
              <div class="add-task-title-row">
                <input
                  autocomplete="off"
                  bind:this={newTaskInput}
                  bind:value={newTaskTitle}
                  type="text"
                  placeholder="Add a task..."
                  class="add-task-input"
                  onkeydown={(e) => {
                    if (e.key === 'Enter' && e.shiftKey) {
                      e.preventDefault();
                      if (!isAddFormExpanded) expandAddForm();
                      else addTask();
                    } else if (e.key === 'Enter') {
                      addTask();
                    } else if (e.key === 'Escape' && isAddFormExpanded) {
                      e.preventDefault();
                      collapseAddForm();
                      newTaskInput?.focus();
                    }
                  }}
                />
                <button
                  type="button"
                  class="add-task-btn"
                  onclick={addTask}
                  disabled={!newTaskTitle.trim() || isAddingTask}>+</button
                >
              </div>
              {#if !isAddFormExpanded}
                <span class="add-task-hint" class:visible={!!newTaskTitle.trim()}>
                  Shift + Enter or <button
                    type="button"
                    class="add-details-link"
                    tabindex={newTaskTitle.trim() ? 0 : -1}
                    aria-hidden={!newTaskTitle.trim()}
                    onclick={expandAddForm}>click here</button
                  > to add details
                </span>
              {/if}

              {#if isAddFormExpanded}
                <textarea
                  bind:this={newTaskNotesInput}
                  bind:value={newTaskNotes}
                  placeholder="Notes (optional)"
                  class="add-task-notes"
                  rows="3"
                  onkeydown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      addTask();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      collapseAddForm();
                      newTaskInput?.focus();
                    }
                  }}></textarea>
                <div class="add-task-toolbar">
                  <div class="add-task-priority-picker">
                    {#each [{ value: 'none' as TaskPriority, icon: '—', label: 'None' }, { value: 'low' as TaskPriority, icon: '🔵', label: 'Low' }, { value: 'medium' as TaskPriority, icon: '🟠', label: 'Medium' }, { value: 'high' as TaskPriority, icon: '🔴', label: 'High' }] as opt}
                      <button
                        type="button"
                        class="add-task-priority-option"
                        class:active={newTaskPriority === opt.value}
                        onclick={() => (newTaskPriority = opt.value)}
                        title={opt.label}
                      >
                        <span class="priority-icon">{opt.icon}</span>
                      </button>
                    {/each}
                  </div>
                  <div class="add-task-actions">
                    <button
                      type="button"
                      class="add-task-cancel"
                      onclick={() => {
                        collapseAddForm();
                        newTaskInput?.focus();
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      class="add-task-submit"
                      onclick={addTask}
                      disabled={!newTaskTitle.trim() || isAddingTask}
                    >
                      Add
                    </button>
                  </div>
                </div>
              {/if}
            </div>
          {/if}

          <!-- Incomplete tasks -->
          {#if incompleteTasks.length === 0 && completedTasks.length === 0}
            <div class="empty-state">
              <span class="empty-icon">📝</span>
              <span class="empty-text">No tasks yet</span>
            </div>
          {:else if isGrouped}
            <!-- Grouped view -->
            {#each taskGroups as group (group.key)}
              {#if group.tasks.length > 0}
                <div class="task-group">
                  <div class="task-group-header">
                    <span class="task-group-icon">{group.icon}</span>
                    <span class="task-group-label">{group.label}</span>
                    <span class="task-group-count">{group.tasks.length}</span>
                    <CopyTasksMenu
                      tasks={group.tasks}
                      heading={`${project?.title ?? 'Tasks'} — ${group.label}`}
                      label={`Copy ${group.label} tasks`}
                    />
                  </div>
                  <div class="task-list" role="list">
                    {#each group.tasks as task (task.id)}
                      {@render taskItem(task, -1)}
                    {/each}
                  </div>
                </div>
              {/if}
            {/each}
          {:else}
            <!-- Flat list view -->
            <div class="task-list" role="list">
              {#each incompleteTasks as task, idx (task.id)}
                {@render taskItem(task, idx)}
              {/each}
            </div>
          {/if}

          <!-- Completed tasks (collapsible) -->
          {#if completedTasks.length > 0}
            <button
              type="button"
              class="completed-toggle"
              onclick={() => {
                showCompleted = !showCompleted;
                persistPrefs({ ...taskViewPrefs, show_completed: showCompleted });
              }}
            >
              <span class="toggle-arrow" class:open={showCompleted}>▶</span>
              Completed ({completedTasks.length})
            </button>

            {#if showCompleted}
              <div class="task-list completed-list" role="list">
                {#each completedTasks as task (task.id)}
                  <div class="task-item completed" role="listitem">
                    {#if !$viewport.isMobile}
                      <span class="drag-handle-spacer"></span>
                    {/if}
                    <button
                      type="button"
                      class="task-checkbox checked"
                      class:wont-do={task.status === 'wont_do'}
                      onclick={() => toggleTask(task)}
                      title={task.status === 'wont_do'
                        ? "Won't do — click to reopen"
                        : 'Done — click to reopen'}
                    >
                      {#if task.status === 'wont_do'}
                        <span class="checkbox-x">✕</span>
                      {:else}
                        <span class="checkbox-check">✓</span>
                      {/if}
                    </button>
                    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
                    <span
                      class="task-title"
                      class:wont-do-text={task.status === 'wont_do'}
                      onclick={(e) => openTaskPopover(task, e.currentTarget as HTMLElement)}
                      role="button"
                      tabindex="0">{task.title}</span
                    >
                    {#if task.notes}
                      <span class="notes-indicator" title="Has notes">
                        <svg viewBox="0 0 16 16" fill="currentColor">
                          <path
                            d="M4 1h8a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1zm1 3v1h6V4H5zm0 3v1h6V7H5zm0 3v1h4v-1H5z"
                          />
                        </svg>
                      </span>
                    {/if}
                    <div class="task-actions" class:menu-open={openMenuId === task.id}>
                      <button
                        type="button"
                        class="task-menu-btn hover-reveal"
                        onclick={(e) => {
                          e.stopPropagation();
                          openTaskMenu(task, -1, e.currentTarget as HTMLElement);
                        }}
                        title="Task actions"
                        aria-label="Actions for {task.title}">⋯</button
                      >
                      <div
                        class="task-menu"
                        class:visible={openMenuId === task.id}
                        class:picking={movePickerTaskId === task.id}
                      >
                        {#if movePickerTaskId === task.id && data}
                          <MoveToProjectPanel
                            excludeProjectId={data.projectId}
                            onSelect={(id, title) => moveTaskToProject(task, id, title)}
                            onBack={() => (movePickerTaskId = null)}
                          />
                        {:else}
                          <div class="menu-label">Priority:</div>
                          <div class="priority-row">
                            <button
                              type="button"
                              class="priority-option"
                              class:active={task.priority === 'none'}
                              onclick={() => setPriority(task, 'none')}
                              title="No priority">—</button
                            >
                            <button
                              type="button"
                              class="priority-option"
                              class:active={task.priority === 'low'}
                              onclick={() => setPriority(task, 'low')}
                              title="Low">🔵</button
                            >
                            <button
                              type="button"
                              class="priority-option"
                              class:active={task.priority === 'medium'}
                              onclick={() => setPriority(task, 'medium')}
                              title="Medium">🟠</button
                            >
                            <button
                              type="button"
                              class="priority-option"
                              class:active={task.priority === 'high'}
                              onclick={() => setPriority(task, 'high')}
                              title="High">🔴</button
                            >
                          </div>
                          <div class="menu-divider"></div>
                          <NowToggle {task} />
                          <button
                            type="button"
                            class="menu-item"
                            onclick={() => handleDuplicateTask(task)}
                          >
                            <Icon name="duplicate" />Duplicate task
                          </button>
                          <TaskCopyActionRow {task} onDone={closeMenus} />
                          <button
                            type="button"
                            class="menu-item menu-item-danger"
                            onclick={() => requestDeleteTask(task)}
                          >
                            <Icon name="trash" />Delete
                          </button>
                          <div class="menu-divider"></div>
                          {#if data}
                            <RecentMoveMenuItem
                              currentProjectId={data.projectId}
                              onMove={(projectId, projectTitle) =>
                                moveTaskToRecent(task, projectId, projectTitle)}
                            />
                          {/if}
                          <button
                            type="button"
                            class="menu-item"
                            onclick={(e) => {
                              e.stopPropagation();
                              movePickerTaskId = task.id;
                            }}
                          >
                            <Icon name="move-to-project" />Move to project…
                            <Icon name="chevron-right" size={12} class="menu-trail" />
                          </button>
                          <button
                            type="button"
                            class="menu-item"
                            onclick={() => convertTaskToProject(task)}
                          >
                            <Icon name="convert" />Convert to project
                          </button>
                        {/if}
                      </div>
                    </div>
                  </div>
                {/each}
              </div>
            {/if}
          {/if}
        {/if}
      </div>

      <!-- Footer -->
      <footer class="rail-footer">
        <button
          type="button"
          class="footer-link"
          onclick={() => router.get(`/projects/${project!.id}`)}
        >
          Open full project page →
        </button>
      </footer>
    </aside>
  {/if}
</ResponsiveOverlay>

{#if bulkMovePickerOpen && bulkMoveAnchorEl && data}
  <MoveToProjectPicker
    excludeProjectId={data.projectId}
    anchorEl={bulkMoveAnchorEl}
    onSelect={(id, title) => {
      bulkMoveTargetProjectId = id;
      bulkMoveTargetProjectTitle = title;
      bulkMovePickerOpen = false;
      showBulkMoveConfirm = true;
    }}
    onClose={() => {
      bulkMovePickerOpen = false;
    }}
  />
{/if}

<BulkMoveTasksConfirmModal
  open={showBulkMoveConfirm}
  sourceProjectTitle={project?.title || 'Current project'}
  targetProjectTitle={bulkMoveTargetProjectTitle}
  taskCount={incompleteTasks.length}
  onCancel={() => {
    showBulkMoveConfirm = false;
    bulkMoveTargetProjectId = null;
    bulkMoveTargetProjectTitle = '';
  }}
  onConfirm={confirmBulkMoveIncompleteTasks}
/>

<!-- Task detail popover -->
<TaskDetailPopover
  task={popoverTask}
  anchorEl={popoverAnchorEl}
  {project}
  initialMenu={popoverMenu}
  menuActions={popoverMenuActions}
  onClose={closeTaskPopover}
  onUpdate={handlePopoverUpdate}
  onDelete={(task, pendingUpdates) => {
    if (pendingUpdates) handlePopoverUpdate(task, pendingUpdates);
    closeTaskPopover();
    deleteTask(task);
  }}
  onMarkWontDo={(task, pendingUpdates) => {
    closeTaskPopover();
    markWontDoWithUpdates(task, pendingUpdates);
  }}
  onToggleComplete={(task, pendingUpdates) => {
    closeTaskPopover();
    toggleTaskWithUpdates(task, pendingUpdates);
  }}
/>

<!-- Mobile: the project's ⋯ menu -->
{#if $viewport.isMobile && project}
  <ActionSheet
    open={projectMenuOpen}
    onClose={() => (projectMenuOpen = false)}
    title={project.title}
    actions={projectMoveActions(project)}
  />
{/if}

<!-- Reorder confirmation modal -->
{#if pendingReorder}
  {@const sortLabel =
    taskViewPrefs.sort_by === 'created_at' ? 'date created' : taskViewPrefs.sort_by}
  <!-- svelte-ignore a11y_no_static_element_interactions, a11y_click_events_have_key_events -->
  <div class="confirm-overlay" onclick={cancelReorder}>
    <!-- svelte-ignore a11y_no_static_element_interactions, a11y_click_events_have_key_events -->
    <div class="confirm-dialog" onclick={(e) => e.stopPropagation()}>
      <p class="confirm-message">Switch to manual ordering?</p>
      <p class="confirm-subtext">
        Tasks are currently sorted by <strong>{sortLabel}</strong>{#if isGrouped}
          and grouped by <strong>{taskViewPrefs.group_by}</strong>{/if}. Moving this task will clear
        those settings and switch to manual drag ordering.
      </p>
      <div class="confirm-actions">
        <button type="button" class="confirm-cancel-btn" onclick={cancelReorder}>Cancel</button>
        <button type="button" class="confirm-ok-btn" onclick={confirmReorder}>Switch & Move</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .project-rail {
    width: 18rem;
    background: #f9fafb;
    border-left: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    max-height: 100%;
  }

  /* Board overlay: stay clear of the canvas toolbar and bottom-left minimap. */
  .project-rail.floating {
    position: absolute;
    z-index: 10;
    top: 6.25rem;
    right: auto;
    bottom: 9.75rem;
    left: 0.75rem;
    width: min(18rem, calc(100% - 1.5rem));
    max-height: none;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    overflow: hidden;
  }

  .rail-header {
    padding: 0.625rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    background: white;
  }

  .project-rail.archived .rail-header {
    background: #f8fafc;
    border-bottom-color: #cbd5e1;
  }

  .project-rail.archived .header-title,
  .project-rail.archived .header-notes {
    color: #475569;
  }

  .header-title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.375rem;
    margin-bottom: 0.25rem;
  }

  .header-title {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.3;
    word-break: break-word;
    min-width: 0;
    flex: 1;
  }

  .close-btn {
    width: 1.25rem;
    height: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s;
  }

  .close-btn:hover {
    color: #374151;
    background: rgba(0, 0, 0, 0.1);
  }

  .header-meta {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-bottom: 0.375rem;
    font-size: 0.625rem;
    color: #6b7280;
  }

  .header-status {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-weight: 500;
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .header-category-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.0625rem 0.3125rem;
    border-radius: 9999px;
    font-size: 0.5625rem;
    font-weight: 600;
    white-space: nowrap;
  }

  .meta-sep {
    color: #d1d5db;
    font-size: 0.5rem;
  }

  .archived-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    padding: 0.06rem 0.35rem;
    border-radius: 9999px;
    font-size: 0.56rem;
    font-weight: 600;
    color: #334155;
    background: #e2e8f0;
    border: 1px solid #cbd5e1;
  }

  .priority-star {
    font-size: 0.625rem;
    line-height: 1;
  }

  .header-notes {
    font-size: 0.6875rem;
    color: #6b7280;
    line-height: 1.4;
    margin-bottom: 0.375rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .header-actions {
    display: flex;
    gap: 0.375rem;
  }

  .header-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.12s;
  }

  .header-action-btn:hover {
    color: #374151;
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  .rail-content {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem 0.75rem;
  }

  .tasks-section-header {
    margin-bottom: 0.5rem;
  }

  .tasks-top-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.25rem;
  }

  .tasks-top-row h3 {
    font-size: 0.6875rem;
    font-weight: 700;
    color: #374151;
  }

  .tasks-top-actions {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .progress-row {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-bottom: 0.25rem;
  }

  .progress-bar-track {
    flex: 1;
    height: 3px;
    background: #e5e7eb;
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-bar-fill {
    height: 100%;
    background: #3b82f6;
    border-radius: 2px;
    transition: width 0.3s ease;
    min-width: 0;
  }

  .progress-label {
    font-size: 0.5625rem;
    color: #9ca3af;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }

  .active-view-chips {
    display: flex;
    gap: 0.25rem;
    flex-wrap: wrap;
    margin-bottom: 0.125rem;
  }

  .view-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.0625rem 0.1875rem 0.0625rem 0.3125rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #2563eb;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.1875rem;
  }

  .chip-dismiss {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 0.75rem;
    height: 0.75rem;
    font-size: 0.5rem;
    color: #93c5fd;
    background: none;
    border: none;
    border-radius: 0.125rem;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    transition: all 0.1s;
  }

  .chip-dismiss:hover {
    color: #1d4ed8;
    background: rgba(37, 99, 235, 0.1);
  }

  /* Grouped view */
  .task-group {
    margin-bottom: 0.5rem;
  }

  .task-group-header {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.25rem;
    margin-bottom: 0.125rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .task-group-icon {
    font-size: 0.5625rem;
  }

  .task-group-label {
    font-size: 0.5625rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .task-group-count {
    font-size: 0.5rem;
    color: #9ca3af;
    margin-left: auto;
  }

  .loading-state {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    justify-content: center;
    padding: 1.5rem 0;
    font-size: 0.6875rem;
    color: #9ca3af;
  }

  .loading-spinner {
    animation: spin 1s linear infinite;
    display: inline-block;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .add-task-row {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.5rem;
  }

  .add-task-row.expanded {
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    padding: 0.375rem;
    background: #fafbff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .add-task-title-row {
    display: flex;
    gap: 0.25rem;
    align-items: center;
  }

  .add-task-input {
    flex: 1;
    padding: 0.25rem 0.375rem;
    font-size: 0.6875rem;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    outline: none;
    transition: border-color 0.15s;
    min-width: 0;
  }

  .add-task-row.expanded .add-task-input {
    border-color: transparent;
    background: transparent;
    padding-left: 0.125rem;
    font-weight: 600;
  }

  .add-task-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
  }

  .add-task-row.expanded .add-task-input:focus {
    border-color: transparent;
    box-shadow: none;
  }

  .add-task-hint {
    font-size: 0.5625rem;
    color: #9ca3af;
    white-space: nowrap;
    user-select: none;
    line-height: 1;
    height: 0.75rem;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.15s;
  }

  .add-task-hint.visible {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  .add-details-link {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: #3b82f6;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 1px;
  }

  .add-details-link:hover {
    color: #2563eb;
  }

  .add-task-btn {
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: background 0.15s;
    flex-shrink: 0;
  }

  .add-task-row.expanded .add-task-btn {
    display: none;
  }

  .add-task-btn:hover:not(:disabled) {
    background: #2563eb;
  }

  .add-task-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .add-task-notes {
    width: 100%;
    padding: 0.375rem;
    font-size: 0.6875rem;
    color: #4b5563;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    outline: none;
    resize: none;
    line-height: 1.4;
    font-family: inherit;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;
  }

  .add-task-notes:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.25);
  }

  .add-task-notes::placeholder {
    color: #9ca3af;
  }

  .add-task-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.25rem;
    border-top: 1px solid #e5e7eb;
    padding-top: 0.25rem;
  }

  .add-task-priority-picker {
    display: flex;
    gap: 0.125rem;
  }

  .add-task-priority-option {
    width: 1.375rem;
    height: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.5625rem;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.1s;
    padding: 0;
  }

  .add-task-priority-option:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  .add-task-priority-option.active {
    background: #eff6ff;
    border-color: #3b82f6;
  }

  .add-task-actions {
    display: flex;
    gap: 0.25rem;
    align-items: center;
  }

  .add-task-cancel {
    padding: 0.1875rem 0.375rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.1s;
  }

  .add-task-cancel:hover {
    color: #374151;
    background: #f3f4f6;
  }

  .add-task-submit {
    padding: 0.1875rem 0.5rem;
    font-size: 0.5625rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: background 0.15s;
  }

  .add-task-submit:hover:not(:disabled) {
    background: #2563eb;
  }

  .add-task-submit:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1.5rem 0;
    color: #9ca3af;
    gap: 0.25rem;
  }

  .empty-icon {
    font-size: 1.25rem;
  }
  .empty-text {
    font-size: 0.6875rem;
  }

  .task-list {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .task-item {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.25rem;
    border-radius: 0.25rem;
    transition: background 0.1s;
  }

  .task-item:hover {
    background: rgba(0, 0, 0, 0.03);
  }

  .task-item.drag-over {
    border-top: 2px solid #3b82f6;
  }

  .task-item.completed > .task-title {
    color: #9ca3af;
  }

  .task-item.completed > .drag-handle-spacer,
  .task-item.completed > .task-checkbox,
  .task-item.completed > .notes-indicator {
    opacity: 0.5;
  }

  .drag-handle {
    width: 0.75rem;
    font-size: 0.625rem;
    color: #d1d5db;
    background: none;
    border: none;
    cursor: grab;
    padding: 0;
    line-height: 1;
    flex-shrink: 0;
    text-align: center;
    transition: color 0.15s;
  }

  .drag-handle:hover {
    color: #9ca3af;
  }

  .drag-handle:focus {
    outline: none;
  }

  .drag-handle-spacer {
    width: 0.75rem;
    flex-shrink: 0;
  }

  .task-checkbox {
    width: 0.875rem;
    height: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid var(--cb-border, #d1d5db);
    border-radius: 0.1875rem;
    background: white;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s;
    padding: 0;
  }

  .task-checkbox:hover {
    background: var(--cb-hover-bg, rgba(209, 213, 219, 0.12));
  }

  .task-checkbox.checked {
    background: #3b82f6;
    border-color: #3b82f6;
  }

  .task-checkbox.wont-do {
    background: #9ca3af;
    border-color: #9ca3af;
  }

  .checkbox-inner {
    width: 0;
    height: 0;
  }

  .checkbox-check,
  .checkbox-x {
    font-size: 0.5625rem;
    color: white;
    font-weight: 700;
    line-height: 1;
  }

  .task-title {
    flex: 1;
    font-size: 0.6875rem;
    color: #1f2937;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.125rem;
    transition: background 0.1s;
  }

  .task-title:hover {
    background: rgba(59, 130, 246, 0.08);
  }

  .task-title.wont-do-text {
    text-decoration: line-through;
    color: #9ca3af;
  }

  .notes-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 0.75rem;
    height: 0.75rem;
    color: #9ca3af;
    flex-shrink: 0;
    margin-right: 0.125rem;
  }

  .notes-indicator svg {
    width: 100%;
    height: 100%;
  }

  /* Task actions dropdown */
  .task-actions {
    position: relative;
  }

  .task-menu-btn {
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6875rem;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.125rem;
    cursor: pointer;
    opacity: 0.35;
    transition: all 0.15s;
  }

  .task-item:hover .task-menu-btn {
    opacity: 1;
    color: #6b7280;
  }

  .task-menu-btn:hover {
    color: #374151;
    background: rgba(0, 0, 0, 0.08);
  }

  .task-menu-btn:focus-visible {
    opacity: 1;
    color: #374151;
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  .task-menu {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.125rem;
    min-width: 10rem;
    max-height: 16rem;
    overflow-y: auto;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 60;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.1s,
      visibility 0.1s;
  }

  .task-menu.visible {
    opacity: 1;
    visibility: visible;
  }

  .menu-open .task-menu-btn {
    opacity: 1;
  }

  .menu-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    padding: 0.375rem 0.5rem;
    font-size: 0.6875rem;
    color: #374151;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
  }

  .menu-item:hover {
    background: #f3f4f6;
  }

  .menu-item-danger {
    color: #dc2626;
  }

  .menu-item-danger:hover {
    background: #fef2f2;
  }

  .menu-item :global(.icon) {
    color: #6b7280;
  }

  .menu-item-danger :global(.icon) {
    color: inherit;
  }

  .menu-item :global(.menu-trail) {
    margin-left: auto;
    color: #9ca3af;
  }

  /* "Move to project…" swaps the menu's items for a project search. */
  .task-menu.picking {
    width: 16rem;
    max-height: none;
    overflow: hidden;
  }

  .menu-divider {
    height: 1px;
    background: #e5e7eb;
    margin: 0.125rem 0;
  }

  .menu-label {
    padding: 0.25rem 0.5rem;
    font-size: 0.5625rem;
    color: #9ca3af;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .priority-row {
    display: flex;
    gap: 0.125rem;
    padding: 0.125rem 0.5rem 0.25rem;
  }

  .priority-option {
    width: 1.5rem;
    height: 1.375rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.1s;
  }

  .priority-option:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  .priority-option.active {
    background: #eff6ff;
    border-color: #3b82f6;
  }

  .completed-toggle {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    padding: 0.375rem 0;
    margin-top: 0.5rem;
    font-size: 0.625rem;
    font-weight: 600;
    color: #6b7280;
    background: none;
    border: none;
    border-top: 1px solid #e5e7eb;
    cursor: pointer;
    text-align: left;
    transition: color 0.15s;
  }

  .completed-toggle:hover {
    color: #374151;
  }

  .toggle-arrow {
    font-size: 0.5rem;
    transition: transform 0.15s;
  }

  .toggle-arrow.open {
    transform: rotate(90deg);
  }

  .completed-list {
    margin-top: 0.25rem;
  }

  .rail-footer {
    padding: 0.375rem 0.75rem;
    border-top: 1px solid #e5e7eb;
    background: white;
    text-align: center;
  }

  .footer-link {
    font-size: 0.625rem;
    color: #2563eb;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.15s;
  }

  .footer-link:hover {
    text-decoration: underline;
    color: #1d4ed8;
  }

  /* Delete confirmation modal */
  .confirm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }

  .confirm-dialog {
    background: white;
    border-radius: 0.5rem;
    padding: 1.25rem;
    min-width: 16rem;
    max-width: 20rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  .confirm-message {
    font-size: 0.8125rem;
    color: #1f2937;
    margin-bottom: 0.25rem;
  }

  .confirm-subtext {
    font-size: 0.6875rem;
    color: #9ca3af;
    margin-bottom: 1rem;
  }

  .confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .confirm-cancel-btn {
    padding: 0.375rem 0.75rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #374151;
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: background 0.15s;
  }

  .confirm-cancel-btn:hover {
    background: #e5e7eb;
  }

  .confirm-ok-btn {
    padding: 0.375rem 0.75rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: background 0.15s;
  }

  .confirm-ok-btn:hover {
    background: #2563eb;
  }

  /* Mobile bottom sheet (ResponsiveOverlay): the sheet provides the frame. */
  .project-rail.in-sheet {
    flex: 1 1 auto;
    min-height: 0;
    border-left: none;
    background: white;
  }

  .project-rail.in-sheet .rail-header {
    padding-top: 0;
  }

  .project-rail.in-sheet .header-title-row {
    align-items: center;
    gap: 0.5rem;
  }

  .project-rail.in-sheet .rail-content {
    min-height: 0;
    overscroll-behavior: contain;
  }

  /* ⋯ beside the sheet's close chip, drawn the same way (32px chip, 44px hit area) */
  .project-menu-btn {
    position: relative;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    color: #4b5563;
    background: #f3f4f6;
    border: none;
    border-radius: 9999px;
    cursor: pointer;
  }

  .project-menu-btn::after {
    content: '';
    position: absolute;
    inset: -6px;
  }

  .project-menu-btn:active {
    background: #e5e7eb;
  }

  /* Task rows: 44px tap targets; ⋯ always visible (there is no hover) */
  .project-rail.in-sheet .task-item {
    min-height: var(--mobile-tap);
  }

  .project-rail.in-sheet .task-menu-btn {
    width: var(--mobile-tap);
    height: var(--mobile-tap);
    margin: -0.25rem -0.5rem -0.25rem 0;
    font-size: 1rem;
    color: #6b7280;
    opacity: 1;
  }

  .project-rail.in-sheet .rail-footer {
    padding: 0;
  }

  .project-rail.in-sheet .footer-link {
    min-height: var(--mobile-tap);
    width: 100%;
    font-size: 0.8125rem;
  }
</style>
